import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CUSTOM_PIECE_CONFIGS } from './CustomPieceShape';
import { Save, RotateCw, Trash2, X, Puzzle } from 'lucide-react';

export default function CustomPiecePropertiesPanel({ 
  piece, 
  onUpdate, 
  onDelete,
  onClose 
}) {
  const [form, setForm] = useState({
    label: '',
    seats: 4,
    rotation: 0,
    width: '',
    height: '',
  });
  const [saving, setSaving] = useState(false);

  // Get piece config
  const config = piece ? CUSTOM_PIECE_CONFIGS[piece.piece_type] : null;

  // Initialize form when piece changes
  useEffect(() => {
    if (piece && config) {
      setForm({
        label: piece.label || config.label || '',
        seats: piece.seats || config.defaultSeats || 4,
        rotation: piece.rotation || 0,
        width: piece.width || config.width || '',
        height: piece.height || config.height || '',
      });
    }
  }, [piece, config]);

  if (!piece) {
    return (
      <div className="bg-slate-900/50 p-4 rounded-lg border border-blue-900/30 h-full">
        <div className="flex items-center justify-center h-full text-slate-500">
          <p className="text-sm text-center">
            Select a custom piece on the canvas<br />to edit its properties
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onUpdate(piece.id, {
        label: form.label,
        seats: parseInt(form.seats) || 4,
        rotation: parseInt(form.rotation) || 0,
        width: form.width ? parseInt(form.width) : null,
        height: form.height ? parseInt(form.height) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = () => {
    const newRotation = (form.rotation + 45) % 360;
    setForm(prev => ({ ...prev, rotation: newRotation }));
    onUpdate(piece.id, { rotation: newRotation });
  };

  return (
    <div className="bg-slate-900/50 p-4 rounded-lg border border-blue-900/30 h-full overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Puzzle className="h-4 w-4 text-blue-400" />
          Piece Properties
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-6 w-6 p-0 hover:bg-slate-700"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Piece Type (Read-only) */}
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Piece Type</Label>
          <div className="text-sm text-blue-400 bg-blue-900/20 px-3 py-2 rounded border border-blue-900/30">
            {config?.label || piece.piece_type}
          </div>
        </div>

        {/* Label */}
        <div className="space-y-1.5">
          <Label htmlFor="label" className="text-xs text-slate-400">Label</Label>
          <Input
            id="label"
            value={form.label}
            onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-sm"
            placeholder="Enter label..."
          />
        </div>

        {/* Seats */}
        <div className="space-y-1.5">
          <Label htmlFor="seats" className="text-xs text-slate-400">Number of Seats</Label>
          <Input
            id="seats"
            type="number"
            min="1"
            max="20"
            value={form.seats}
            onChange={(e) => setForm(prev => ({ ...prev, seats: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-sm"
          />
        </div>

        {/* Size Controls */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="width" className="text-xs text-slate-400">Width</Label>
            <Input
              id="width"
              type="number"
              min={config?.minWidth || 40}
              value={form.width}
              onChange={(e) => setForm(prev => ({ ...prev, width: e.target.value }))}
              className="bg-slate-800 border-slate-600 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="height" className="text-xs text-slate-400">Height</Label>
            <Input
              id="height"
              type="number"
              min={config?.minHeight || 40}
              value={form.height}
              onChange={(e) => setForm(prev => ({ ...prev, height: e.target.value }))}
              className="bg-slate-800 border-slate-600 text-sm"
            />
          </div>
        </div>

        {/* Rotation */}
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Rotation: {form.rotation}°</Label>
          <div className="flex items-center gap-2">
            <Input
              type="range"
              min="0"
              max="359"
              value={form.rotation}
              onChange={(e) => setForm(prev => ({ ...prev, rotation: parseInt(e.target.value) }))}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRotate}
              className="border-slate-600 hover:bg-slate-700"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            type="submit"
            size="sm"
            disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onDelete(piece.id)}
            className="border-red-900/50 text-red-400 hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {/* Info section */}
      <div className="mt-4 pt-4 border-t border-slate-700">
        <p className="text-xs text-slate-500">
          Tip: Select multiple pieces with Shift+Click, then click "Group" to create a custom table.
        </p>
      </div>
    </div>
  );
}
